import { useMemo, useState } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Header } from "./Header";

interface WelcomeScreenProps {
  onContinue: () => void;
  onClose: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  logoUrlAlt?: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  productTitle?: string;
  productHandle?: string;
  customerTags?: string;
}

export function WelcomeScreen({
  onContinue,
  onClose,
  language = "en",
  logoUrl,
  logoUrlAlt,
  heroImageUrl,
  heroImageAlt,
  productTitle,
  productHandle,
  customerTags,
}: WelcomeScreenProps) {
  const t = getNamespaceTranslations(language, "welcome");
  const [selectedImprovements, setSelectedImprovements] = useState<string[]>([]);
  const [selectionError, setSelectionError] = useState("");

  // Check if customer has active subscription for this product
  const hasActiveSubscription = () => {
    if (!productTitle || !customerTags) return false;

    const tags = customerTags.split(',').map(tag => tag.trim());
    const productName = productTitle.trim();

    // Check if customer has Active_{ProductName} tag
    const activeTag = `Active_${productName}`;
    return tags.includes(activeTag);
  };

  const isActiveSubscriber = hasActiveSubscription();
  const normalizedProductSignals = `${productHandle || ""} ${productTitle || ""}`.toLowerCase();
  const isFemaleProduct =
    normalizedProductSignals.includes("for-her") ||
    normalizedProductSignals.includes("para-ella");
  const isMaleProduct =
    normalizedProductSignals.includes("for-him") ||
    normalizedProductSignals.includes("para-el");

  const warmupOptions = useMemo(
    () => {
      if (isFemaleProduct) {
        return [
          { label: t.femaleOptionLibido || "", testId: "warmup-answer-libido" },
          { label: t.femaleOptionConfidence || "", testId: "warmup-answer-confidence" },
          { label: t.femaleOptionSexLife || "", testId: "warmup-answer-sex-life" },
        ].filter((option) => Boolean(option.label));
      }

      if (isMaleProduct) {
        return [
          { label: t.maleOptionPerformance || "", testId: "warmup-answer-performance" },
          { label: t.maleOptionAchievingErection || "", testId: "warmup-answer-achieving-erection" },
          { label: t.maleOptionMaintainingErection || "", testId: "warmup-answer-maintaining-erection" },
        ].filter((option) => Boolean(option.label));
      }

      return [
        { label: t.optionSexLife, testId: "warmup-answer-sex-life" },
        { label: t.optionConfidence, testId: "warmup-answer-confidence" },
        { label: t.optionRelationship, testId: "warmup-answer-relationship" },
      ].filter((option) => Boolean(option.label));
    },
    [
      isFemaleProduct,
      isMaleProduct,
      t.femaleOptionLibido,
      t.femaleOptionConfidence,
      t.femaleOptionSexLife,
      t.maleOptionPerformance,
      t.maleOptionAchievingErection,
      t.maleOptionMaintainingErection,
      t.optionSexLife,
      t.optionConfidence,
      t.optionRelationship,
    ]
  );

  const toggleWarmupOption = (option: string) => {
    setSelectedImprovements((prev) =>
      prev.includes(option) ? prev.filter((value) => value !== option) : [...prev, option]
    );
    if (selectionError) setSelectionError("");
  };

  const handleContinue = () => {
    if (selectedImprovements.length === 0) {
      setSelectionError(
        t.selectionRequired ||
          (language === "es" ? "Este campo es obligatorio" : "This field is required")
      );
      const firstOption = document.querySelector<HTMLElement>(
        `[data-testid="${warmupOptions[0]?.testId}"]`
      );
      firstOption?.focus();
      firstOption?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    setSelectionError("");
    onContinue();
  };

  return (
    <div className="w-full min-h-screen flex flex-col">
      {/* Header with Logo - White Background */}
      <Header logoUrl={logoUrl} logoAlt={logoUrlAlt} onClose={onClose} />

      {/* Main Content - Gradient Background */}
      <main
        className="flex-1 flex items-start lg:items-center justify-center py-[30px] px-5 lg:px-4 lg:py-8 overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #FAFAFA 0%, #E8F0F3 100%)'
        }}
      >
        <div className="w-full max-w-[640px]">
          {/* Mobile: Text First, Desktop: Image First */}
          <div className="flex flex-col lg:flex-col">
            {/* Text Content - Shows first on mobile, second on desktop */}
            <div className="text-left space-y-[10px] lg:order-2 lg:mt-6">
              <h1
                className="text-2xl leading-[30px] font-normal lg:text-[30px] lg:leading-[36px]"
                style={{ fontFamily: 'TT Commons Pro' }}
              >
                {t.title}
              </h1>
              <p
                className="text-black text-base leading-[22px]"
                style={{ fontFamily: 'TT Commons Pro' }}
              >
                {t.description}
              </p>
              <div
                className="pt-2 space-y-3"
                role="group"
                aria-label={t.description}
                aria-invalid={!!selectionError}
              >
                {warmupOptions.map((option) => {
                  const isSelected = selectedImprovements.includes(option.label);
                  return (
                    <button
                      key={option.testId}
                      type="button"
                      onClick={() => toggleWarmupOption(option.label)}
                      className={`w-full text-left px-4 py-3 rounded-2xl border text-base leading-[22px] transition-colors ${
                        isSelected
                          ? "bg-white text-black border-2 border-[#2D55FB]"
                          : selectionError
                            ? "bg-white text-black border border-[#E34D52]"
                            : "bg-white text-black border-[#D9D9D9] hover:border-[#2D55FB]"
                      }`}
                      style={{ fontFamily: "TT Commons Pro" }}
                      aria-pressed={isSelected}
                      data-testid={option.testId}
                    >
                      {option.label}
                    </button>
                  );
                })}
                {selectionError && (
                  <p
                    className="text-[#E34D52] text-sm"
                    role="alert"
                    style={{ fontFamily: "TT Commons Pro" }}
                  >
                    {selectionError}
                  </p>
                )}
              </div>
            </div>

            {/* Action Button - Shows third on mobile and desktop */}
            <div className="mt-[30px] lg:order-3 w-full">
              {/* Continue Button / Go to Account Button */}
              {isActiveSubscriber ? (
                <a
                  href="/account"
                  className="w-full h-[50px] inline-flex items-center justify-center rounded-full bg-[#2D55FB] text-white px-[30px] text-lg leading-6 font-medium transition-colors hover:bg-[#2347E8]"
                  style={{ fontFamily: 'TT Commons Pro' }}
                  aria-label={t.goToAccount || 'Patient Portal'}
                >
                  {t.goToAccount || 'Patient Portal'}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleContinue}
                  className="w-full h-[50px] inline-flex items-center justify-center rounded-full bg-[#2D55FB] text-white px-[30px] text-lg leading-6 font-medium transition-colors hover:bg-[#2347E8]"
                  style={{ fontFamily: 'TT Commons Pro' }}
                  aria-label={t.continue}
                  data-testid="product_start-continue_button"
                >
                  {t.continue}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
